import { useState } from "react";
import { Icon } from "@/components/ui/Icon";
import { StatusPill, type PillStyle } from "@/components/ui/StatusPill";
import { PersonSearchList } from "@/components/board/PersonPicker";
import { columnIconName } from "@/lib/columnIcon";
import { t } from "@/lib/strings";
import type { CellValue, ColumnType, LeadTimeUnit, UUID } from "@/lib/types";
import type { ViewStatusOption } from "@/lib/boardView";

/** A board column that can be bulk-set across the current selection. */
export interface BulkEditableColumn {
  id: string;
  name: string;
  type: ColumnType;
  /** status / tags option set (labels drive tags, ids drive status). */
  options?: ViewStatusOption[];
}

interface Props {
  columns: BulkEditableColumn[];
  workspaceId: UUID | null;
  boardId: UUID | null;
  pillStyle: PillStyle;
  buttonStyle: React.CSSProperties;
  /** Fan the chosen value out to every selected row, then clear the selection. */
  onApply: (columnId: string, value: CellValue) => void;
}

const LEAD_UNITS: { unit: LeadTimeUnit; label: string }[] = [
  { unit: "days", label: t.leadUnitDays },
  { unit: "weeks", label: t.leadUnitWeeks },
  { unit: "months", label: t.leadUnitMonths },
];

const menuPanel: React.CSSProperties = {
  position: "absolute",
  bottom: "calc(100% + 8px)",
  left: 0,
  zIndex: 32,
  background: "var(--surface-elev)",
  border: "1px solid var(--hairline-strong)",
  borderRadius: 10,
  boxShadow: "0 12px 30px rgba(13,62,99,0.22)",
  padding: 6,
  minWidth: 232,
  maxHeight: 360,
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const rowBtn: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  textAlign: "left",
  background: "transparent",
  border: "none",
  borderRadius: 6,
  padding: "6px 8px",
  cursor: "pointer",
  color: "var(--ink-1)",
  fontSize: 13,
  fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
};

const applyBtn: React.CSSProperties = {
  ...rowBtn,
  justifyContent: "center",
  background: "var(--brand-blue, #00599b)",
  color: "#fff",
  fontWeight: 600,
  marginTop: 4,
};

const softBtn: React.CSSProperties = {
  ...rowBtn,
  justifyContent: "center",
  border: "1px solid var(--hairline-strong)",
};

function hover(on: boolean) {
  return (e: React.MouseEvent<HTMLElement>) => {
    e.currentTarget.style.background = on ? "var(--surface-soft)" : "transparent";
  };
}

export function BulkFieldMenu({
  columns,
  workspaceId,
  boardId,
  pillStyle,
  buttonStyle,
  onApply,
}: Props) {
  const [open, setOpen] = useState(false);
  const [col, setCol] = useState<BulkEditableColumn | null>(null);
  // Per-editor draft state (reset whenever a column is (re)selected).
  const [tagSel, setTagSel] = useState<Set<string>>(new Set());
  const [dateVal, setDateVal] = useState("");
  const [ltValue, setLtValue] = useState("");
  const [ltUnit, setLtUnit] = useState<LeadTimeUnit>("weeks");
  const [people, setPeople] = useState<UUID[]>([]);
  const [pickPeople, setPickPeople] = useState(false);

  if (columns.length === 0) return null;

  const close = () => {
    setOpen(false);
    setCol(null);
    setPickPeople(false);
  };

  const pick = (c: BulkEditableColumn) => {
    setCol(c);
    setTagSel(new Set());
    setDateVal("");
    setLtValue("");
    setLtUnit("weeks");
    setPeople([]);
    setPickPeople(false);
  };

  const apply = (value: CellValue) => {
    if (col) onApply(col.id, value);
    close();
  };

  return (
    <div style={{ position: "relative" }}>
      <button
        style={buttonStyle}
        onClick={() => {
          if (open) close();
          else setOpen(true);
        }}
      >
        <Icon name="edit" size={13} /> {t.bulkSetField}
      </button>
      {open ? (
        <>
          {/* Dismiss-backdrop, now ALWAYS present. It used to be suppressed while the person
              sub-popover was open (its outside-click would otherwise tear down the whole menu
              and lose the draft) — which meant that, mid-selection, clicking away did nothing at
              all. That was part of the same flakiness. The list is INSIDE the menu now, so a
              click on it never reaches this. */}
          <div style={{ position: "fixed", inset: 0, zIndex: 31 }} onClick={close} />
          <div style={menuPanel}>
            {col === null ? (
              columns.map((c) => (
                <button
                  key={c.id}
                  style={rowBtn}
                  onClick={() => pick(c)}
                  onMouseEnter={hover(true)}
                  onMouseLeave={hover(false)}
                >
                  <Icon name={columnIconName(c.type)} size={14} color="var(--ink-3)" stroke={1.8} />
                  {c.name}
                </button>
              ))
            ) : (
              <>
                <button
                  style={{ ...rowBtn, color: "var(--ink-3)", fontSize: 12 }}
                  onClick={() => setCol(null)}
                  onMouseEnter={hover(true)}
                  onMouseLeave={hover(false)}
                >
                  <Icon name="chevRight" size={12} /> {col.name}
                </button>

                {col.type === "status"
                  ? (col.options ?? [])
                      .filter((o) => !o.archived)
                      .map((o) => (
                        <button
                          key={o.id}
                          style={rowBtn}
                          onClick={() => apply(o.id)}
                          onMouseEnter={hover(true)}
                          onMouseLeave={hover(false)}
                        >
                          <StatusPill
                            option={{ color: o.color, label: o.label }}
                            pillStyle={pillStyle}
                          />
                        </button>
                      ))
                  : null}

                {col.type === "tags" ? (
                  <>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "4px 6px" }}>
                      {(col.options ?? []).map((o) => {
                        const on = tagSel.has(o.label);
                        return (
                          <button
                            key={o.id}
                            onClick={() =>
                              setTagSel((prev) => {
                                const next = new Set(prev);
                                if (next.has(o.label)) next.delete(o.label);
                                else next.add(o.label);
                                return next;
                              })
                            }
                            style={{
                              border: `1px solid ${on ? "var(--brand-blue, #00599b)" : "var(--hairline-strong)"}`,
                              background: on ? "var(--surface-soft)" : "transparent",
                              borderRadius: 999,
                              padding: "3px 10px",
                              fontSize: 12,
                              cursor: "pointer",
                              color: "var(--ink-1)",
                            }}
                          >
                            {o.label}
                          </button>
                        );
                      })}
                    </div>
                    <button style={applyBtn} onClick={() => apply([...tagSel])}>
                      {t.bulkApply}
                    </button>
                    <button style={softBtn} onClick={() => apply([])}>
                      {t.bulkClearValue}
                    </button>
                  </>
                ) : null}

                {col.type === "date" ? (
                  <>
                    <input
                      type="date"
                      value={dateVal}
                      onChange={(e) => setDateVal(e.target.value)}
                      style={{
                        margin: "4px 6px",
                        padding: "6px 8px",
                        border: "1px solid var(--hairline-strong)",
                        borderRadius: 6,
                        background: "var(--surface)",
                        color: "var(--ink-1)",
                        fontSize: 13,
                      }}
                    />
                    <button style={applyBtn} disabled={!dateVal} onClick={() => apply(dateVal)}>
                      {t.bulkApply}
                    </button>
                    <button style={softBtn} onClick={() => apply(null)}>
                      {t.bulkClearValue}
                    </button>
                  </>
                ) : null}

                {col.type === "lead_time" ? (
                  <>
                    <div style={{ display: "flex", gap: 6, padding: "4px 6px" }}>
                      <input
                        type="number"
                        min={1}
                        value={ltValue}
                        onChange={(e) => setLtValue(e.target.value)}
                        style={{
                          width: 64,
                          padding: "6px 8px",
                          border: "1px solid var(--hairline-strong)",
                          borderRadius: 6,
                          background: "var(--surface)",
                          color: "var(--ink-1)",
                          fontSize: 13,
                        }}
                      />
                      <select
                        value={ltUnit}
                        onChange={(e) => setLtUnit(e.target.value as LeadTimeUnit)}
                        style={{
                          flex: 1,
                          padding: "6px 8px",
                          border: "1px solid var(--hairline-strong)",
                          borderRadius: 6,
                          background: "var(--surface)",
                          color: "var(--ink-1)",
                          fontSize: 13,
                        }}
                      >
                        {LEAD_UNITS.map((u) => (
                          <option key={u.unit} value={u.unit}>
                            {u.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      style={applyBtn}
                      disabled={!(Math.floor(Number(ltValue)) > 0)}
                      onClick={() => apply({ value: Math.floor(Number(ltValue)), unit: ltUnit })}
                    >
                      {t.bulkApply}
                    </button>
                    <button style={softBtn} onClick={() => apply(null)}>
                      {t.bulkClearValue}
                    </button>
                  </>
                ) : null}

                {col.type === "person" ? (
                  <>
                    <button
                      style={rowBtn}
                      // A TOGGLE, not a one-way door. It used to be `setPickPeople(true)`, so
                      // once the list was open there was no way to fold it away again — and
                      // with the old floating popover it was sitting on top of Übernehmen, so
                      // there was nothing to click either. Escape also closes it.
                      onClick={() => setPickPeople((v) => !v)}
                      onMouseEnter={hover(true)}
                      onMouseLeave={hover(false)}
                    >
                      <Icon name="person" size={14} color="var(--ink-3)" />
                      {people.length > 0 ? `${people.length} ${t.selected}` : t.bulkPickPeople}
                    </button>
                    {/* 🔴 INLINE, not a Popover. This used to render <PersonPicker>, which
                        anchors a floating list to the button directly ABOVE — so the list
                        landed ON TOP of the Übernehmen button below it. A colleague could
                        select people and then had nothing to press ("kann ich nicht
                        übernehmen"), and whether the button was reachable depended on where
                        the popover flipped, which is why it felt flaky and why the panels
                        appeared to fly around the window. In normal flow the list simply
                        pushes the buttons down, and they are always there. */}
                    {pickPeople ? (
                      <div
                        style={{
                          margin: "4px 0 6px",
                          border: "1px solid var(--hairline-soft)",
                          borderRadius: 8,
                          overflow: "hidden",
                          background: "var(--surface)",
                        }}
                      >
                        <PersonSearchList
                          value={people}
                          allowMultiple
                          {...(workspaceId ? { workspaceId } : {})}
                          {...(boardId ? { boardId } : {})}
                          onChange={setPeople}
                          onClose={() => setPickPeople(false)}
                        />
                      </div>
                    ) : null}
                    <button
                      style={applyBtn}
                      disabled={people.length === 0}
                      onClick={() => apply(people)}
                    >
                      {t.bulkApply}
                    </button>
                    <button style={softBtn} onClick={() => apply([])}>
                      {t.bulkClearValue}
                    </button>
                  </>
                ) : null}
              </>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
